import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Activity, Brain, Heart, TrendingUp, Apple, Dumbbell } from "lucide-react";

const Landing = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-primary/10" />
        <div className="container relative mx-auto px-4 py-20 md:py-32">
          <div className="mx-auto max-w-4xl text-center animate-fade-in">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-2 text-sm font-medium text-primary">
              <Activity className="h-4 w-4" />
              Your Personal Health Intelligence
            </div>
            <h1 className="mb-6 text-5xl font-bold tracking-tight md:text-7xl">
              Unlock a Healthier You with
              <span className="text-primary"> AI-Powered Insights</span>
            </h1>
            <p className="mb-10 text-lg text-muted-foreground md:text-xl">
              Leverage the power of AI to understand your health risks, receive personalized nutrition and fitness plans, and proactively manage your well-being.
            </p>
            <div className="flex flex-col gap-4 sm:flex-row sm:justify-center">
              <Button asChild size="lg" className="rounded-full shadow-lg hover:shadow-xl transition-all">
                <Link to="/register">Begin Your Journey</Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="rounded-full">
                <Link to="/login">Member Login</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="mb-16 text-center">
            <h2 className="mb-4 text-4xl font-bold">A New Era of Personalized Health</h2>
            <p className="text-lg text-muted-foreground">
              Our intelligent platform provides you with the tools to take control of your health.
            </p>
          </div>
          
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {[
              {
                icon: Brain,
                title: "AI-Driven Risk Analysis",
                description: "Our predictive models analyze your health data to identify potential risks, empowering you with early insights.",
              },
              {
                icon: Apple,
                title: "Tailored Nutrition Plans",
                description: "Receive customized meal plans that align with your health goals, dietary needs, and preferences.",
              },
              {
                icon: Dumbbell,
                title: "Adaptive Fitness Routines",
                description: "Get dynamic workout recommendations that evolve with your fitness level and health objectives.",
              },
              {
                icon: Heart,
                title: "Comprehensive Vitals Tracking",
                description: "Monitor your blood pressure, glucose, cholesterol, and other key metrics in one unified dashboard.",
              },
              {
                icon: TrendingUp,
                title: "Actionable Progress Insights",
                description: "Visualize your health journey with intuitive charts and data-driven feedback to stay motivated.",
              },
              {
                icon: Activity,
                title: "Proactive Daily Guidance",
                description: "Receive timely tips and reminders to help you build and maintain healthy habits for life.",
              }
            ].map((feature, index) => (
              <div
                key={index}
                className="group relative overflow-hidden rounded-2xl bg-card p-8 shadow-sm transition-all hover:shadow-lg animate-scale-in card-hover"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className={`absolute inset-0 bg-primary opacity-0 transition-opacity group-hover:opacity-5`} />
                <div className="relative">
                  <div className={`mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary text-primary-foreground`}>
                    <feature.icon className="h-6 w-6" />
                  </div>
                  <h3 className="mb-3 text-xl font-semibold">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="relative overflow-hidden rounded-3xl bg-primary p-12 text-center text-primary-foreground shadow-2xl md:p-20">
            <div className="relative z-10">
              <h2 className="mb-6 text-4xl font-bold md:text-5xl">
                Ready to Revolutionize Your Health?
              </h2>
              <p className="mb-8 text-lg opacity-90 md:text-xl">
                Join our community and start your journey towards a healthier, more informed life today.
              </p>
              <Button asChild size="lg" variant="secondary" className="rounded-full shadow-lg hover:shadow-xl">
                <Link to="/register">Sign Up for Free</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t py-12">
        <div className="container mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>© 2025 HealthAI. All rights reserved. Empowering healthier lives through intelligent technology.</p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
